<script setup>

import { ref, watch, computed, onMounted } from 'vue'
import Dialog from 'primevue/dialog'
import { Form as VeeForm, Field, ErrorMessage } from "vee-validate"
import * as yup from "yup"
import rentingService from '@/services/renting.service'
import propertyService from '@/services/property.service'
import Button from 'primevue/button'
import Icon from '@/components/Icon/Icon.vue'
import ProgressSpinner from 'primevue/progressspinner'
import { createToast } from 'mosha-vue-toastify'
import '@vuepic/vue-datepicker/dist/main.css'
import moment from 'moment'
import { useDataStore } from '@/store/data.store'

const visible = ref(false)
const inProgress = ref(false)
const dataStore = useDataStore()
const referenceIndexes = ref([])

const props = defineProps({
    externVisible: {
        type: Boolean,
        required: true,
        default: false,
    },
    externReferenceIndex: {
        type: Object,
        required: false,
        default: {}
    },
})

const emit = defineEmits(['close', 'edit'])

function cancel() {
    visible.value = false
}

watch(() => props.externVisible, (newVal) => {
    visible.value = newVal
})

watch(visible, (newVal) => {
    visible.value = newVal

    if(visible.value == false) {
        emit('close')
    }
})

const handleUpdate = (form) => {
    inProgress.value = true

    emit('edit', props.externReferenceIndex)

    inProgress.value = false
    visible.value = false

    createToast("L'index a été mis à jour",
    {
        type: 'success',
        showIcon: false,
        hideProgressBar: true,
        timeout: 4000,
    })
}

onMounted(() => {
    dataStore.getReferenceIndexes()
        .then(response => referenceIndexes.value = response)
})

</script>

<template>
    <div>
        <Dialog
            v-model:visible="visible"
            modal
            header="Modifer le nouvel index"
            :style="{ width: '30rem' }"
        >
            <template #header>
                <div class="w-full px-4 py-2 cursor-move text-2xl border-b border-rose-400">
                    <span class="font-bold whitespace-nowrap">Modifer le nouvel index</span>
                </div>
            </template>
            
            <div class="mx-4">
                <div class="m-0 px-8 py-8 relative bg-white">
                    <VeeForm @submit="handleUpdate" class="flex flex-col gap-4">
                        <Field id="uuid" v-model="props.externReferenceIndex.id" autocomplete="off" name="uuid" class="w-full form-control form-input" type="hidden" />

                        <div class="flex items-center gap-4 mb-2">
                            <label for="rent" class="font-semibold w-24">IRL</label>
                            <Field
                                name="reference_index_id"
                                as="select"
                                class="form-input w-full p-2 bg-white"
                                v-model="props.externReferenceIndex.id"
                            >
                                <option value="" disabled>Sélectionner l'IRL</option>
                                <option value="">Aucun</option>
                                <option
                                    v-for="referenceIndex in referenceIndexes"
                                    :key="referenceIndex.id"
                                    :value="referenceIndex.id"
                                >{{ referenceIndex.period }} / {{ referenceIndex.irl_index }}</option>
                            </Field>
                        </div>
                        <ErrorMessage name="reference_index_id" v-slot="{ message }">
                            <div class="border border-red-400 text-red-700 px-4 py-0 mt-1 rounded relative" role="alert">
                                {{ message }}
                            </div>
                        </ErrorMessage>

                        <div class="flex items-center gap-4 mb-2">
                            <label for="rent" class="font-semibold w-24">Arrondir</label>
                            <Field
                                name="round_rent"
                                type="checkbox"
                                :value="true"
                                :unchecked-value="false"
                                class="peer/round"
                                v-model="props.externReferenceIndex.roundRent"
                            />
                            
                        </div>
                        <ErrorMessage name="round_rent" v-slot="{ message }">
                            <div class="border border-red-400 text-red-700 px-4 py-0 mt-1 rounded relative" role="alert">
                                {{ message }}
                            </div>
                        </ErrorMessage>

                        <div class="flex justify-center gap-4">
                            <Button type="button" @click="cancel()" class="px-4 py-2 mt-8 w-32 rounded flex gap-2 justify-center shadow-md border border-black hover:shadow-none hover:bg-gray-100">
                                <Icon name="cancel" :size="12" class="w-4 h-4" /> Annuler
                            </Button>
                            <Button :disabled="inProgress" type="submit" class="px-4 py-2 mt-8 w-32 rounded flex gap-2 justify-center shadow-md border border-black bg-green-300 hover:shadow-none hover:bg-green-500">
                                <span v-if="!inProgress">
                                    <Icon name="edit" :size="12" class="w-4 h-4" /> Modifier
                                </span>
                                <ProgressSpinner
                                    v-else
                                    style="width: 20px; height: 20px"
                                    strokeWidth="5"
                                    fill="transparent"
                                    animationDuration="2.5s"
                                    aria-label="Custom ProgressSpinner"
                                    class="m-auto"
                                />
                            </Button>
                        </div>
                    </VeeForm>
                </div>
            </div>
        </Dialog>
    </div>
</template>

<style>

.dp__input {
    border-radius: 0.375rem;
    padding-top: 0.25rem;
    padding-bottom: 0.25rem;
}

.dp__input:hover {
    border-color: #94a3b8;
    transition-property: color, background-color, border-color, text-decoration-color, fill, stroke, opacity, box-shadow, transform, filter, backdrop-filter;
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
    transition-duration: 150ms;
}

</style>